import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { v2, type NotificationApi } from '@/api/v2Client';

// Phase 5.X-notifications-system — single notification row component.
//
// Used by both the dropdown (compact mode) and the full /notifications
// page (expanded mode). The visual difference is set by `compact`:
//   - compact=true: smaller font, message context, no per-row actions
//   - compact=false: full Body + per-row Mark-read + Delete buttons
//
// Click behavior (both modes):
//   - If `link` is set: invoke `onClick` (which marks read + navigates).
//   - Otherwise: invoke `onClick` (which marks read only).

const COMPACT_ROW_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 12px',
  cursor: 'pointer',
  background: 'transparent',
  textAlign: 'left',
  width: '100%',
  border: 'none',
  borderBottom: '1px solid var(--border, #e5e7eb)',
  borderRadius: 0,
};

const FULL_ROW_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '12px 16px',
  borderBottom: '1px solid var(--border, #e5e7eb)',
};

const TITLE_STYLE: CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  overflowWrap: 'anywhere',
};

const META_STYLE: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted, #6b7280)',
};

const ACTION_BTN_STYLE: CSSProperties = {
  fontSize: 11,
  background: 'transparent',
  color: 'var(--accent, #2563eb)',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const deltaSec = Math.floor((Date.now() - then) / 1000);
  if (deltaSec < 60) return 'just now';
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  if (deltaSec < 2592000) return `${Math.floor(deltaSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface NotificationItemProps {
  readonly notification: NotificationApi;
  readonly compact?: boolean;
  readonly alternate?: boolean;
  /** Click handler — fires the "mark read + navigate" composite action. */
  readonly onClick: () => void;
  /** Optional per-row mark-read (full mode only, hidden in compact). */
  readonly onMarkRead?: () => void;
  /** Optional per-row delete (full mode only, hidden in compact). */
  readonly onDelete?: () => void;
}

export function NotificationItem({
  notification,
  compact = false,
  alternate = false,
  onClick,
  onMarkRead,
  onDelete,
}: NotificationItemProps): ReactElement {
  const isUnread = notification.readAt === null;
  // Existing submission updates store their product/sequence prefix in Body.
  // Promote it for both views, including notifications already in the inbox.
  const context = notification.relatedEntityType === 'Submission'
    ? /^(.* · Sequence (?:\d{4}|—)): /s.exec(notification.body ?? '')
    : null;
  const recordedLabel = context?.[1];
  const submissionId = notification.relatedEntityType === 'Submission' ? notification.relatedEntityId : null;
  const [resolved, setResolved] = useState<{ id: string; label: string } | null>(null);
  useEffect(() => {
    if (!submissionId || recordedLabel) return;
    let stale = false;
    // Legacy messages may lack context. Use the existing authorized endpoint.
    v2.submissions.get(submissionId).then((submission) => {
      if (!stale) setResolved({ id: submissionId, label: `${submission.DrugName} · Sequence ${submission.SequenceNumber || '—'}` });
    }).catch(() => {
      if (!stale) setResolved({ id: submissionId, label: 'Submission unavailable' });
    });
    return () => { stale = true; };
  }, [submissionId, recordedLabel]);
  const label = recordedLabel ?? (resolved?.id === submissionId ? resolved?.label : null);
  const title = label ? `${label} — ${notification.title}` : notification.title;
  const body = context ? notification.body?.slice(context[0].length) : notification.body;
  const baseStyle = compact ? COMPACT_ROW_STYLE : FULL_ROW_STYLE;
  const rowStyle: CSSProperties = {
    ...baseStyle,
    background: alternate ? 'var(--bg, #f3f4f6)' : 'var(--surface, #ffffff)',
    boxShadow: isUnread ? 'inset 3px 0 0 var(--primary, #1b3a6b)' : 'none',
  };

  if (compact) {
    // Compact mode uses the row AS the click target (button).
    return (
      <button
        type="button"
        data-testid={`notification-item-${notification.id}`}
        data-unread={isUnread ? 'true' : 'false'}
        onClick={onClick}
        style={rowStyle}
      >
        <span style={TITLE_STYLE}>{title}</span>
        {body && (
          <span style={{ fontSize: 12, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
            {body}
          </span>
        )}
        <span style={META_STYLE}>{formatRelative(notification.createdAt)}</span>
      </button>
    );
  }

  // Full mode renders Title + Body + per-row actions.
  return (
    <div
      data-testid={`notification-item-${notification.id}`}
      data-unread={isUnread ? 'true' : 'false'}
      style={rowStyle}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={TITLE_STYLE}>{title}</span>
        <span style={META_STYLE}>{formatRelative(notification.createdAt)}</span>
      </div>
      {body && (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45 }}>{body}</p>
      )}
      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        {notification.link && (
          <Link
            data-testid={`notification-link-${notification.id}`}
            to={notification.link}
            onClick={onClick}
            style={{ ...ACTION_BTN_STYLE, textDecoration: 'underline' }}
          >
            View
          </Link>
        )}
        {isUnread && onMarkRead && (
          <button
            type="button"
            data-testid={`notification-mark-read-${notification.id}`}
            onClick={onMarkRead}
            style={ACTION_BTN_STYLE}
          >
            Mark read
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            data-testid={`notification-delete-${notification.id}`}
            onClick={onDelete}
            style={{ ...ACTION_BTN_STYLE, color: 'var(--danger, #b91c1c)' }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
